import type {
  AnalyticsFilter,
  CompanyAnalytics,
  TeamAnalytics,
} from '@/types/analytics';
import type {
  CoachAssignedMember,
  CoachBooking,
  CoachDirectoryEntry,
  CoachIdentity,
  CoachLabel,
  CoachRoster,
  CoachScope,
  CoachScoreboard,
  CoachTrends,
  CompanyCoachAssignment,
} from '@/types/coach';
import type { Objective } from '@/types/objective';
import type { PerformanceLoopResponse } from '@/types/performance-loop';

import type { piloteerApi } from './piloteer';

type CoachBuilder = Parameters<
  Parameters<typeof piloteerApi.injectEndpoints>[0]['endpoints']
>[0];

// Null replaces the last successful response after ANY failed refresh. RTK Query
// otherwise keeps that response, including after consent/assignment revocation.
const sensitive = {
  keepUnusedDataFor: 0,
  providesTags: ['Coach' as const],
  async onQueryStarted(
    _arg: unknown,
    lifecycle: {
      queryFulfilled: PromiseLike<unknown>;
      updateCachedData: (recipe: () => null) => unknown;
    }
  ) {
    try {
      await lifecycle.queryFulfilled;
    } catch {
      lifecycle.updateCachedData(() => null);
    }
  },
};
const companyQuery = (companyUuid?: string) =>
  companyUuid ? `?company_uuid=${encodeURIComponent(companyUuid)}` : '';
const scopePath = ({ scope, uuid }: CoachScope) =>
  `coach/${scope === 'user' ? 'employees' : scope === 'company' ? 'companies' : 'teams'}/${encodeURIComponent(uuid)}`;
// Admin configuration is not member performance data: fetch on mount and keep
// it current through mutation invalidation instead of polling.
export const coachAdminQueryOptions = {
  refetchOnMountOrArgChange: true,
} as const;
export const coachQueryOptions = {
  refetchOnMountOrArgChange: true,
  refetchOnFocus: true,
  refetchOnReconnect: true,
  pollingInterval: 15000,
} as const;
const analyticsQuery = (
  filters: AnalyticsFilter & { includeMembers?: boolean } = {}
) => {
  const query = new URLSearchParams();
  if (filters.startDate) query.set('start_date', filters.startDate);
  if (filters.endDate) query.set('end_date', filters.endDate);
  if (filters.dimensions?.length)
    query.set('dimensions', filters.dimensions.join(','));
  if (filters.minMeetings !== undefined && filters.minMeetings > 0)
    query.set('min_meetings', String(filters.minMeetings));
  if (filters.includeMembers !== undefined)
    query.set('include_members', String(filters.includeMembers));
  return query.toString();
};
export const coachEndpoints = (builder: CoachBuilder) => ({
  getCoachCompanyAnalytics: builder.query<
    CompanyAnalytics | null,
    { uuid: string; filters?: AnalyticsFilter & { includeMembers?: boolean } }
  >({
    ...sensitive,
    query: ({ uuid, filters }) =>
      `coach/companies/${encodeURIComponent(uuid)}/analytics?${analyticsQuery(filters)}`,
  }),
  getCoachTeamAnalytics: builder.query<
    TeamAnalytics | null,
    { uuid: string; filters?: AnalyticsFilter & { includeMembers?: boolean } }
  >({
    ...sensitive,
    query: ({ uuid, filters }) =>
      `coach/teams/${encodeURIComponent(uuid)}/analytics?${analyticsQuery(filters)}`,
  }),
  getMyCoaches: builder.query<CoachBooking[] | null, void>({
    ...sensitive,
    query: () => 'users/me/coaches',
  }),
  getCoachProfile: builder.query<
    (CoachIdentity & { role: string }) | null,
    void
  >({ ...sensitive, query: () => 'coach/profile' }),
  getCoachAssignments: builder.query<
    CoachRoster | null,
    {
      page?: number;
      perPage?: number;
      search?: string;
      dataSharingEnabled?: boolean;
      companyUuid?: string;
    }
  >({
    ...sensitive,
    query: (params) => {
      const query = new URLSearchParams({
        page: String(params.page ?? 1),
        per_page: String(params.perPage ?? 20),
      });
      if (params.search) query.set('search', params.search);
      if (params.dataSharingEnabled !== undefined)
        query.set('data_sharing_enabled', String(params.dataSharingEnabled));
      if (params.companyUuid) query.set('company_uuid', params.companyUuid);
      return `coach/assignments?${query}`;
    },
  }),
  getCoachCompanies: builder.query<CoachLabel[] | null, void>({
    ...sensitive,
    query: () => 'coach/companies',
  }),
  getCoachTeams: builder.query<CoachLabel[] | null, string>({
    ...sensitive,
    query: (uuid) => `coach/companies/${encodeURIComponent(uuid)}/teams`,
  }),
  getCoachScoreboard: builder.query<CoachScoreboard | null, CoachScope>({
    ...sensitive,
    query: (arg) =>
      `${scopePath(arg)}/scoreboard?period_days=${arg.periodDays}`,
  }),
  getCoachTrends: builder.query<CoachTrends | null, CoachScope>({
    ...sensitive,
    query: (arg) => `${scopePath(arg)}/trends?period_days=${arg.periodDays}`,
  }),
  getCoachObjectives: builder.query<Objective[] | null, string>({
    ...sensitive,
    query: (uuid) => `coach/employees/${encodeURIComponent(uuid)}/objectives`,
  }),
  getCoachPerformanceLoop: builder.query<
    PerformanceLoopResponse | null,
    { uuid: string; periodDays: number }
  >({
    ...sensitive,
    query: ({ uuid, periodDays }) =>
      `coach/employees/${encodeURIComponent(uuid)}/performance-loop?period_days=${periodDays}`,
  }),
  getCompanyCoaches: builder.query<CoachIdentity[] | null, string>({
    ...sensitive,
    query: (companyUuid) => `coaching/coaches${companyQuery(companyUuid)}`,
  }),
  getCoachAssignmentsAdmin: builder.query<
    CoachAssignedMember[] | null,
    { coachUuid: string; companyUuid: string }
  >({
    ...sensitive,
    query: ({ coachUuid, companyUuid }) =>
      `coaching/coaches/${encodeURIComponent(coachUuid)}/assignments${companyQuery(companyUuid)}`,
  }),
  getCompanyCoachAssignments: builder.query<
    CompanyCoachAssignment[] | null,
    string
  >({
    ...sensitive,
    query: (companyUuid) =>
      `coaching/assignments?company_uuid=${encodeURIComponent(companyUuid)}`,
  }),
  assignMemberToCoach: builder.mutation<
    { status: string },
    { coachUuid: string; employeeUuid: string }
  >({
    query: ({ coachUuid, employeeUuid }) => ({
      url: `coaching/coaches/${encodeURIComponent(coachUuid)}/assignments`,
      method: 'POST',
      body: { employee_uuid: employeeUuid },
    }),
    invalidatesTags: ['Coach'],
  }),
  unassignMemberFromCoach: builder.mutation<
    { status: string },
    { coachUuid: string; employeeUuid: string }
  >({
    query: ({ coachUuid, employeeUuid }) => ({
      url: `coaching/coaches/${encodeURIComponent(coachUuid)}/assignments/${encodeURIComponent(employeeUuid)}`,
      method: 'DELETE',
    }),
    invalidatesTags: ['Coach'],
  }),
  adminListCoaches: builder.query<CoachDirectoryEntry[] | null, void>({
    ...sensitive,
    query: () => 'admin/coaches',
  }),
  adminCreateCoach: builder.mutation<
    CoachIdentity,
    { name: string; email: string; bookingUrl?: string }
  >({
    query: ({ name, email, bookingUrl }) => ({
      url: 'admin/coaches',
      method: 'POST',
      body: { name, email, booking_url: bookingUrl || null },
    }),
    invalidatesTags: ['Coach'],
  }),
  adminUpdateCoach: builder.mutation<
    CoachIdentity,
    { uuid: string; name: string; bookingUrl: string | null }
  >({
    query: ({ uuid, name, bookingUrl }) => ({
      url: `admin/coaches/${encodeURIComponent(uuid)}`,
      method: 'PATCH',
      body: { name, booking_url: bookingUrl },
    }),
    invalidatesTags: ['Coach'],
  }),
});
